import {
  AnalyticsService as analytics,
  AnalyticsTrackingEvent
} from "@brandclub/common-ui";
import { useCallback, useEffect, useRef } from "react";
import { useLocationTrackingParams } from "../../components/providers/LocationTrackingProvider";

const AnalyticsService = {
  track: (eventName: AnalyticsTrackingEvent, data: any, {
    sendImmediately = false
  }: {
    sendImmediately?: boolean;
  } = {}) => {
    return analytics.track(eventName, data, {
      sendImmediately
    }).catch((error: any) => {
      console.error("Error tracking event", error);
    });
  },
};

interface AdditionalData {
  [key: string]: any;
}

/**
 * useTrackViewEvent: Tracks a view event when component mounts
 */
export const useTrackViewEvent = <T = Record<string, unknown>>(
  eventName: AnalyticsTrackingEvent,
  {
    immediate = false,
    minimumViewTime = 1000
  }: {
    immediate?: boolean;
    minimumViewTime?: number;
  } = {}
) => {
  const [trackingParams] = useLocationTrackingParams();
  const timerRef = useRef<number | null>(null);
  const hasTracked = useRef(false);

  useEffect(() => {
    return () => {
      timerRef.current && clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, []);

  return (additionalData: T, options: {
    sendImmediately?: boolean;
  } = {}) => {
    if (hasTracked.current) {
      return;
    }
    const eventData = {
      ...trackingParams,
      ...additionalData,
    };

    if (immediate || options.sendImmediately) {
      // Immediate mode: track right away
      AnalyticsService.track(eventName, eventData, options);
      hasTracked.current = true;
    } else {
      // Debounced mode: clear previous timer and schedule new one
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      timerRef.current = window.setTimeout(() => {
        AnalyticsService.track(eventName, eventData, options);
        hasTracked.current = true;
        timerRef.current = null;
      }, minimumViewTime);
    }
  };
};

export const useTrackActions = () => {
  const [trackingParams] = useLocationTrackingParams();
  const additionDataRef = useRef<AdditionalData>({});

  const trackAction = useCallback(
    (
      eventName: AnalyticsTrackingEvent,
      additionalData: AdditionalData = {},
      options: {
        sendImmediately?: boolean;
      } = {}
    ) => {
      AnalyticsService.track(eventName, {
        ...trackingParams,
        ...additionDataRef.current,
        ...additionalData,
      }, options);
    },
    [trackingParams]
  );

  const updateAdditionalData = useCallback((additionData: AdditionalData) => {
    additionDataRef.current = { ...additionDataRef.current, ...additionData };
  }, []);

  return [trackAction, updateAdditionalData] as const;
};
